import type { AssessmentShowFeedback } from "../../../axios/quiz";

export type ContestPresetId =
  | "competition_after_answer"
  | "competition_after_completion_editable"
  | "competition_not_show"
  | "practice_after_answer";

export interface ContestPreset {
  id: ContestPresetId;
  label: string;
  contest: {
    answer_editing_enabled: boolean;
    show_feedback: AssessmentShowFeedback;
  };
  gamification: {
    competition_mode: boolean;
  };
}

export const DEFAULT_ANSWERS_TIME_MINUTES = 12;
export const DEFAULT_PRESET_ID: ContestPresetId = "competition_after_answer";

export const CONTEST_PRESETS: ContestPreset[] = [
  {
    id: "competition_after_answer",
    label: "Competition · feedback after answer",
    contest: {
      answer_editing_enabled: false,
      show_feedback: "after_answer",
    },
    gamification: { competition_mode: true },
  },
  {
    id: "competition_after_completion_editable",
    label: "Competition · editable · feedback after completion",
    contest: {
      answer_editing_enabled: true,
      show_feedback: "after_completion",
    },
    gamification: { competition_mode: true },
  },
  {
    id: "competition_not_show",
    label: "Competition · no feedback",
    contest: {
      answer_editing_enabled: false,
      show_feedback: "not_show",
    },
    gamification: { competition_mode: true },
  },
  {
    id: "practice_after_answer",
    label: "Practice · feedback after answer",
    contest: {
      answer_editing_enabled: false,
      show_feedback: "after_answer",
    },
    gamification: { competition_mode: false },
  },
];

export const getPreset = (presetId: ContestPresetId): ContestPreset =>
  CONTEST_PRESETS.find((preset) => preset.id === presetId) ?? CONTEST_PRESETS[0];

export const matchPresetId = (
  assessment: Record<string, unknown>
): ContestPresetId => {
  const contest =
    assessment.contest && typeof assessment.contest === "object"
      ? (assessment.contest as Record<string, unknown>)
      : {};
  const gamification =
    assessment.gamification && typeof assessment.gamification === "object"
      ? (assessment.gamification as Record<string, unknown>)
      : {};

  const answerEditing = Boolean(contest.answer_editing_enabled);
  const showFeedback =
    typeof contest.show_feedback === "string"
      ? contest.show_feedback
      : "after_answer";
  const competitionMode =
    typeof gamification.competition_mode === "boolean"
      ? gamification.competition_mode
      : true;

  const match = CONTEST_PRESETS.find(
    (preset) =>
      preset.contest.answer_editing_enabled === answerEditing &&
      preset.contest.show_feedback === showFeedback &&
      preset.gamification.competition_mode === competitionMode
  );

  return match?.id ?? DEFAULT_PRESET_ID;
};
